import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/projects/IPO/components/ui/card";
import { Badge } from "@/projects/IPO/components/ui/badge";
import { Button } from "@/projects/IPO/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/IPO/components/ui/tabs";
import { userIPOApplications } from "@/projects/IPO/data/ipoData";
import {
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  IndianRupee,
  Calendar,
  FileText,
  AlertCircle,
  Building2,
  Eye,
} from "lucide-react";
import ipoNavigationRoutes from "@/projects/IPO/utils/navigationRoutes";

const IPOApplicationStatusPage = () => {
  const [activeTab, setActiveTab] = useState("current");
  const currentApplications = userIPOApplications.filter(
    app => app.status === "pending"
  );
  const historyApplications = userIPOApplications.filter(
    app => app.status !== "pending"
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="gap-1 border border-amber-200 bg-amber-100 text-amber-700">
            <Clock className="h-3 w-3" /> Pending
          </Badge>
        );
      case "allotted":
        return (
          <Badge className="gap-1 border border-green-200 bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" /> Allotted
          </Badge>
        );
      case "unallocated":
        return (
          <Badge className="gap-1 border border-red-200 bg-red-100 text-red-700">
            <XCircle className="h-3 w-3" /> Unallocated
          </Badge>
        );
      case "refunded":
        return (
          <Badge className="gap-1 border border-blue-200 bg-blue-100 text-blue-700">
            <RefreshCw className="h-3 w-3" /> Refunded
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const stats = {
    total: userIPOApplications.length,
    pending: currentApplications.length,
    allotted: userIPOApplications.filter(a => a.status === "allotted").length,
    totalAmount: userIPOApplications.reduce((sum, a) => sum + a.amount, 0),
  };

  return (
    <div className="via-secondary/3 min-h-screen bg-gradient-to-b from-secondary/5 to-background">
      <div className="container mx-auto px-4 py-8 md:px-6">
        {/* Header */}
        <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="flex items-center gap-3 font-['Raleway'] text-2xl font-bold text-secondary md:text-3xl">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              IPO Application Status
            </h1>
            <p className="mt-2 text-muted-foreground">
              Track all your IPO applications and allotments
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card className="border-border bg-white transition-shadow hover:shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-3">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-secondary">
                    {stats.total}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Total Applications
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-white transition-shadow hover:shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-amber-100 p-3">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">
                    {stats.pending}
                  </p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-white transition-shadow hover:shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-green-100 p-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.allotted}
                  </p>
                  <p className="text-sm text-muted-foreground">Allotted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-white transition-shadow hover:shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-blue-100 p-3">
                  <IndianRupee className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">
                    ₹{(stats.totalAmount / 1000).toFixed(0)}K
                  </p>
                  <p className="text-sm text-muted-foreground">Total Applied</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="border border-border bg-white p-1">
            <TabsTrigger
              value="current"
              className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Clock className="h-4 w-4" />
              Current Status ({currentApplications.length})
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Calendar className="h-4 w-4" />
              History ({historyApplications.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="current">
            <Card className="finease-card overflow-hidden">
              <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-transparent">
                <CardTitle className="flex items-center gap-2 text-lg text-secondary">
                  <Clock className="h-5 w-5 text-primary" />
                  Pending Applications
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {currentApplications.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                      <TrendingUp className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="mb-2 text-lg font-medium text-secondary">
                      No Pending Applications
                    </p>
                    <p className="mb-4 text-muted-foreground">
                      All your IPO applications have been processed
                    </p>
                    <Button variant="outline" asChild>
                      <Link to={ipoNavigationRoutes.IPO_HOME}>
                        Browse Open IPOs
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Type
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Company
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Application No
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Exchange Ref
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            DP ID
                          </th>
                          <th className="p-4 text-center text-sm font-semibold text-secondary">
                            Qty
                          </th>
                          <th className="p-4 text-right text-sm font-semibold text-secondary">
                            Amount
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Status
                          </th>
                          <th className="p-4 text-center text-sm font-semibold text-secondary">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentApplications.map(app => (
                          <tr
                            key={app.id}
                            className="border-b transition-colors hover:bg-muted/20"
                          >
                            <td className="p-4">
                              <Badge variant="outline" className="font-medium">
                                {app.type}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                  <Building2 className="h-4 w-4 text-primary" />
                                </div>
                                <span className="font-semibold text-secondary">
                                  {app.ipoName}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="rounded bg-muted px-2 py-1 font-mono text-xs">
                                {app.applicationNo}
                              </span>
                            </td>
                            <td className="p-4 text-muted-foreground">
                              {app.exchangeRef}
                            </td>
                            <td className="p-4 text-muted-foreground">
                              {app.dpId}
                            </td>
                            <td className="p-4 text-center font-medium text-secondary">
                              {app.qty}
                            </td>
                            <td className="p-4 text-right font-bold text-secondary">
                              ₹{app.amount.toLocaleString()}
                            </td>
                            <td className="p-4">
                              {getStatusBadge(app.status)}
                            </td>
                            <td className="p-4 text-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="finease-card overflow-hidden">
              <CardHeader className="border-b bg-gradient-to-r from-secondary/5 to-transparent">
                <CardTitle className="flex items-center gap-2 text-lg text-secondary">
                  <Calendar className="h-5 w-5 text-primary" />
                  Application History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {historyApplications.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                      <Calendar className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="mb-2 text-lg font-medium text-secondary">
                      No History Found
                    </p>
                    <p className="text-muted-foreground">
                      Your completed IPO applications will appear here
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Type
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            IPO Name
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Application ID
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            DP ID
                          </th>
                          <th className="p-4 text-center text-sm font-semibold text-secondary">
                            Qty
                          </th>
                          <th className="p-4 text-right text-sm font-semibold text-secondary">
                            Amount
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Applied On
                          </th>
                          <th className="p-4 text-left text-sm font-semibold text-secondary">
                            Allotment
                          </th>
                          <th className="p-4 text-center text-sm font-semibold text-secondary">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyApplications.map(app => (
                          <tr
                            key={app.id}
                            className="border-b transition-colors hover:bg-muted/20"
                          >
                            <td className="p-4">
                              <Badge variant="outline" className="font-medium">
                                {app.type}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                  <Building2 className="h-4 w-4 text-primary" />
                                </div>
                                <span className="font-semibold text-secondary">
                                  {app.ipoName}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="rounded bg-muted px-2 py-1 font-mono text-xs">
                                {app.applicationId}
                              </span>
                            </td>
                            <td className="p-4 text-muted-foreground">
                              {app.dpId}
                            </td>
                            <td className="p-4 text-center font-medium text-secondary">
                              {app.qty}
                            </td>
                            <td className="p-4 text-right font-bold text-secondary">
                              ₹{app.amount.toLocaleString()}
                            </td>
                            <td className="p-4 text-muted-foreground">
                              {app.appliedDate
                                ? new Date(app.appliedDate).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "N/A"}
                            </td>
                            <td className="p-4">
                              {getStatusBadge(app.status)}
                            </td>
                            <td className="p-4 text-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Info Alert */}
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 text-blue-600" />
          <div className="text-sm">
            <p className="font-medium text-blue-700">Allotment Information</p>
            <p className="text-blue-600">
              IPO allotments are subject to subscription levels and basis of
              allotment. Check your registered email for allotment
              notifications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IPOApplicationStatusPage;
